import { useSceneEvents } from "@/components/common/scene-events-context";
import { PointCloudObject } from "@/object-cache";
import { AdaptivePointsMaterial } from "@faro-lotv/lotv";
import { useEffect, useMemo } from "react";
import { Color, Vector2, Vector4 } from "three";

/** A height band of points to tint */
export type FloorBand = {
  /** World heights of the band */
  range: [number, number];

  /** Tint color */
  color: string;

  /** How much of the tint replaces the point color, from 0 to 1 */
  strength: number;
};

const MAX_BANDS = 16;

const INJECT_AFTER = "#include <project_vertex>";

const DECLARATIONS = `
uniform vec2 uFloorBands[${MAX_BANDS}];
uniform vec4 uFloorBandColors[${MAX_BANDS}];
uniform int uFloorBandCount;
`;

const TINT = `
#if defined( USE_COLOR ) || defined( USE_COLOR_ALPHA )
	float floorBandY = (modelMatrix * vec4(transformed, 1.0)).y;
	for (int i = 0; i < ${MAX_BANDS}; i++) {
		if (i >= uFloorBandCount) break;
		if (floorBandY >= uFloorBands[i].x && floorBandY <= uFloorBands[i].y) {
			vColor.rgb = mix(vColor.rgb, uFloorBandColors[i].rgb, uFloorBandColors[i].a);
			break;
		}
	}
#endif
`;

/**
 * Tints the points of the point cloud within the bands, by patching the vertex shader of its material in place.
 * Swapping the material would dispose it, and lose the hidden scans and point size settings.
 *
 * @param pointCloud The point cloud to tint
 * @param bands The bands to tint, none to restore the point cloud colors
 */
export function useFloorBandTint(
  pointCloud: PointCloudObject,
  bands: FloorBand[],
): void {
  const { invalidatePointCloudScene } = useSceneEvents();
  const isEnabled = bands.length > 0;

  const uniforms = useMemo(
    () => ({
      uFloorBands: {
        value: Array.from({ length: MAX_BANDS }, () => new Vector2()),
      },
      uFloorBandColors: {
        value: Array.from({ length: MAX_BANDS }, () => new Vector4()),
      },
      uFloorBandCount: { value: 0 },
    }),
    [],
  );

  useEffect(() => {
    const { material } = pointCloud;
    if (!isEnabled || !(material instanceof AdaptivePointsMaterial)) return;
    const original = material.vertexShader;
    material.vertexShader =
      DECLARATIONS + original.replace(INJECT_AFTER, `${INJECT_AFTER}\n${TINT}`);
    Object.assign(material.uniforms, uniforms);
    material.needsUpdate = true;
    invalidatePointCloudScene.emit();
    return () => {
      material.vertexShader = original;
      material.needsUpdate = true;
      invalidatePointCloudScene.emit();
    };
  }, [invalidatePointCloudScene, isEnabled, pointCloud, uniforms]);

  useEffect(() => {
    const color = new Color();
    const shown = bands.slice(0, MAX_BANDS);
    for (const [index, band] of shown.entries()) {
      uniforms.uFloorBands.value[index].set(...band.range);
      color.set(band.color);
      uniforms.uFloorBandColors.value[index].set(
        color.r,
        color.g,
        color.b,
        band.strength,
      );
    }
    uniforms.uFloorBandCount.value = shown.length;
    if (pointCloud.material instanceof AdaptivePointsMaterial) {
      pointCloud.material.uniformsNeedUpdate = true;
    }
    invalidatePointCloudScene.emit();
  }, [bands, invalidatePointCloudScene, pointCloud, uniforms]);
}
